import { useSortable } from '@dnd-kit/sortable';
import { DeleteOutline, DragIndicatorOutlined, EditOutlined } from '@mui/icons-material';
import { ClickAwayListener, Grow, IconButton, MenuItem, MenuList, Paper, Popper, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';

export type VNSortableItemTemplateProps = {
  item: any;
  sortableId: string;
  index: number;
  name: string;
  [key: string]: any; // allow any other props
};

type Props = {
  itemId: string;
  index: number;
  editedItem: any;
  parentName: string;
  ItemTemplate: React.ComponentType<VNSortableItemTemplateProps>;
  itemTemplateProps: any;
  handleMove?: (newIndex: number | 'first' | 'last') => void;
  handleRemove?: () => void;
  handleEdit?: () => void;
  canDelete?: boolean;
  deleteDisabledTooltip?: string;
};

const VNSortableItem = ({
  itemId,
  editedItem,
  index,
  parentName,
  ItemTemplate,
  itemTemplateProps,
  handleMove,
  handleRemove,
  handleEdit,
  canDelete = true,
  deleteDisabledTooltip,
}: Props) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { attributes, listeners, setNodeRef, transform, transition, active } = useSortable({ id: itemId });

  const style = {
    transform: `translate3d(${transform?.x || 0}px, ${transform?.y || 0}px, 0)`,
    transition,
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current?.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleListKeyDown = (event: React.KeyboardEvent) => {
    if (['Tab', 'Escape'].includes(event.key)) {
      event.preventDefault();
      setOpen(false);
    }
  };

  // currying
  const onMove = (direction: number | 'first' | 'last') => () => {
    setOpen(false);
    handleMove?.(direction);
  };

  return (
    <div className={`vn-sortable-item ${active?.id === itemId ? 'active' : ''}`} ref={setNodeRef} style={style} {...attributes}>
      <IconButton
        ref={anchorRef}
        aria-controls={open ? 'composition-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        {...listeners}
        onClick={handleToggle}
        className="drag-button"
        size="small"
      >
        <DragIndicatorOutlined />
      </IconButton>

      <ItemTemplate item={editedItem} sortableId={itemId} index={index} key={itemId} name={parentName} {...itemTemplateProps} />

      <div className="item-actions">
        {handleEdit && (
          <IconButton className="edit-button" size="small" onClick={handleEdit}>
            <EditOutlined fontSize="small" />
          </IconButton>
        )}

        {handleRemove &&
          (deleteDisabledTooltip && !canDelete ? (
            <Tooltip title={deleteDisabledTooltip} placement="top" arrow>
              <div>
                <IconButton className="delete-button" size="small" color="error" onClick={handleRemove} disabled={!canDelete}>
                  <DeleteOutline color="error" fontSize="small" />
                </IconButton>
              </div>
            </Tooltip>
          ) : (
            <IconButton className="delete-button" size="small" color="error" onClick={handleRemove} disabled={!canDelete}>
              <DeleteOutline color="error" fontSize="small" />
            </IconButton>
          ))}
      </div>

      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-start" transition>
        {({ TransitionProps, placement }) => (
          <Grow
            in={TransitionProps.in}
            onEnter={TransitionProps.onEnter}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
            unmountOnExit
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} aria-labelledby="composition-button" onKeyDown={handleListKeyDown}>
                  <MenuItem onClick={onMove('first')}>Move to the very Top</MenuItem>
                  <MenuItem onClick={onMove(-1)}>Move Up</MenuItem>
                  <MenuItem onClick={onMove(1)}>Move Down</MenuItem>
                  <MenuItem onClick={onMove('last')}>Move to the very Bottom</MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default VNSortableItem;
